import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import {
  LoadingOutlined,
  WarningOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { useAppSelector } from "../../store/hooks";
import { useFirstClickFullscreen } from "../../components/profileActions";
import { useMonitorInfo } from "../../hooks/useMonitors";
import { useMenu } from "../../hooks/useMenu";
import { useViewport } from "../../hooks/useViewport";
import Navbar from "./components/Navbar";
import MonitorsContent from "./components/MonitorsContent";
import { StationWebSocketProvider } from "./components/StationWebSocketProvider";
import EdgeModeBanner from "../../components/EdgeModeBanner";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
  danger: "var(--color-danger-ink)",
};

const Monitors = () => {
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  const userId = useAppSelector((state) => state.auth.userId);
  // Първи клик в модула → цял екран (без админския акаунт); не пречи на
  // pointerdown отключването на звука — слушателите са независими.
  useFirstClickFullscreen();
  const [filterMode, setFilterMode] = useState<"unfulfilled" | "fulfilled">(
    "unfulfilled",
  );
  // Разделен екран: коя половина е активна (държи селекцията, клавиатурата и
  // footer-а). Клик в панел го активира; стрелките не сменят панела.
  const [activePane, setActivePane] = useState<"left" | "right">("left");

  // Зареждаме менюто за да имаме преводи на продукти и станции
  useMenu();

  const {
    data: monitorInfo = null,
    isLoading: loading,
    error: monitorError,
  } = useMonitorInfo(userId ?? null);
  // Извличаме detail-a от FastAPI грешка вместо да показваме "AxiosError: …"
  const extractErrorMessage = (err: unknown): string => {
    if (err instanceof AxiosError) {
      const detail = (err.response?.data as { detail?: unknown } | undefined)
        ?.detail;
      if (typeof detail === "string" && detail.length > 0) return detail;
    }
    return String(err);
  };
  const error = monitorError
    ? extractErrorMessage(monitorError)
    : !userId
      ? "User ID not found"
      : null;

  const uniqueStationIds = monitorInfo?.stationIds
    ? Array.from(new Set(monitorInfo.stationIds))
    : [];

  // Разделен екран само когато настройката е включена И двете половини имат
  // станции — иначе падаме на един екран (например стар BE без полетата).
  const leftStationIds = monitorInfo?.leftStationIds ?? [];
  const rightStationIds = monitorInfo?.rightStationIds ?? [];
  const splitScreen =
    (monitorInfo?.splitScreen ?? false) &&
    leftStationIds.length > 0 &&
    rightStationIds.length > 0;

  // Общите настройки на двата панела (и на единствения при цял екран).
  const contentProps = {
    filterMode,
    aggregatedQuantities: monitorInfo?.aggregatedQuantities ?? false,
    completeFullQuantity: monitorInfo?.completeFullQuantity ?? false,
    view: monitorInfo?.view ?? "grid",
    isShpicker: monitorInfo?.isShpicker ?? false,
    showAllStatuses: monitorInfo?.showAllStatuses ?? false,
    colorBy: monitorInfo?.colorBy ?? "table",
    // За етикета на частта при продукт на няколко станции.
    stationNames: monitorInfo?.stations ?? [],
  } as const;

  return (
    <StationWebSocketProvider stationIds={uniqueStationIds}>
      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: R.bg,
          overflow: "hidden",
        }}
      >
        {/* Локален режим (без интернет, срещу касата) — docs/edge.md */}
        <EdgeModeBanner />
        <Navbar
          stations={monitorInfo?.stations || []}
          filterMode={filterMode}
          onFilterChange={setFilterMode}
          isShpicker={monitorInfo?.isShpicker ?? false}
          splitScreen={splitScreen}
          leftStationIds={leftStationIds}
          rightStationIds={rightStationIds}
        />

        <main
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            padding: isNarrow ? "14px 14px 80px" : "22px 28px 88px",
          }}
        >
          {loading && <StateCard kind="loading" />}

          {error && <StateCard kind="error" message={error} />}

          {!loading && !error && monitorInfo && (
            <>
              {monitorInfo.stationIds.length === 0 ? (
                <StateCard kind="no-stations" />
              ) : (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    minHeight: 0,
                  }}
                >
                  {splitScreen ? (
                    /* Разделен екран: две половини със свои станции; navbar-ът
                       (табовете) и footer-ът са общи — табът филтрира двата
                       панела, footer-ът действа в активния. */
                    <div className="flex min-h-0 flex-1 gap-[14px] overflow-hidden">
                      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                        <MonitorsContent
                          {...contentProps}
                          paneStationIds={leftStationIds}
                          isActive={activePane === "left"}
                          onActivate={() => setActivePane("left")}
                        />
                      </div>
                      <div className="w-px shrink-0 bg-shade-3" />
                      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                        <MonitorsContent
                          {...contentProps}
                          paneStationIds={rightStationIds}
                          isActive={activePane === "right"}
                          onActivate={() => setActivePane("right")}
                        />
                      </div>
                    </div>
                  ) : (
                    <MonitorsContent {...contentProps} />
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </StationWebSocketProvider>
  );

  function StateCard({
    kind,
    message,
  }: {
    kind: "loading" | "error" | "no-stations";
    message?: string;
  }) {
    const tokens =
      kind === "error"
        ? { chipBg: "var(--color-danger-tint)", chipInk: R.danger, icon: <WarningOutlined /> }
        : { chipBg: R.brandTint, chipInk: R.brandDeep, icon: kind === "loading" ? <LoadingOutlined spin /> : <ShopOutlined /> };

    const kicker =
      kind === "loading"
        ? t("common.loading")
        : kind === "error"
          ? t("common.error")
          : t("monitors.noAssignedStations");

    const sub =
      kind === "loading"
        ? null
        : kind === "error"
          ? message
          : t("monitors.contactAdmin");

    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: R.surface,
            border: `1px solid ${R.border}`,
            borderRadius: 14,
            padding: "28px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            minWidth: 260,
            maxWidth: 420,
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: tokens.chipBg,
              color: tokens.chipInk,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
            }}
          >
            {tokens.icon}
          </div>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: R.textMuted,
              letterSpacing: 1.2,
              textTransform: "uppercase",
            }}
          >
            {kicker}
          </div>
          {sub && (
            <div
              style={{
                fontSize: 13,
                color: R.textSoft,
                lineHeight: 1.45,
              }}
            >
              {sub}
            </div>
          )}
        </div>
      </div>
    );
  }
};

export default Monitors;
